import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import type {
  CanvasSyncRunEvent,
  EventLevel,
  GetRunResponse,
  GetSisBundleResponse,
  RelatedRunSummary,
  UploadSummary,
} from "@canvas-sync/shared";
import { SisBundleStatusBadge } from "../components/SisBundleStatusBadge";
import { compareToGroup } from "../lib/inputComparison";
import { api } from "../api/client";
import { CsvPreviewModal } from "../components/CsvPreviewModal";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { StatusBadge } from "../components/StatusBadge";
import { UploadStatusBadge } from "../components/UploadStatusBadge";
import {
  formatBytes,
  formatDateTime,
  formatDuration,
  formatIdLabel,
  formatMetaValue,
  formatRelative,
  formatTime,
} from "../lib/format";
import { triggerLabel, uploadTriggerLabel } from "../lib/labels";
import { POLL, usePoll } from "../lib/usePoll";
import { isRunStuck, stuckThresholdMinutes } from "../lib/runStatus";
import { useRole } from "../lib/roleContext";
import { TermDisplay } from "../components/TermBadge";
import type { ExpectedTerms } from "../lib/terms";

const TERMINAL = new Set(["SUCCEEDED", "FAILED", "CANCELLED"]);
const FAILURE_STATES = new Set(["FAILED", "CANCELLED"]);
const ALL_LEVELS: EventLevel[] = ["DEBUG", "INFO", "WARN", "ERROR"];

export function RunDetailPage() {
  const { runId } = useParams<{ runId: string }>();
  const nav = useNavigate();
  const { isAdmin } = useRole();
  const [run, setRun] = useState<GetRunResponse | null>(null);
  const [events, setEvents] = useState<CanvasSyncRunEvent[]>([]);
  const [uploads, setUploads] = useState<UploadSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [downloadErr, setDownloadErr] = useState<string | null>(null);
  const [rerunning, setRerunning] = useState(false);
  const [rerunErr, setRerunErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  // Warn (but allow) before re-posting a fix already shipped via a SIS bundle.
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  // Preview-modal state. `kind` distinguishes input CSV from fix-CSV output
  // so the modal title reads naturally; `url` is the live presigned URL.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewKind, setPreviewKind] = useState<"input" | "output" | null>(
    null,
  );
  const [previewErr, setPreviewErr] = useState<string | null>(null);
  // Current global default — the Rerun button label reflects this so the
  // user sees the *live* policy from the Settings page, not the historical
  // value the source run was created with.
  const [globalAutoSubmit, setGlobalAutoSubmit] = useState<boolean | null>(
    null,
  );
  // Expected terms for the Term meta entry's mismatch highlight — same
  // display-only validation as the Syncs table.
  const [expectedTerms, setExpectedTerms] = useState<ExpectedTerms>({});
  // When this run's fix shipped inside a SIS bundle, fetch that bundle once
  // so the submissions card can show its status + Canvas import ID with a
  // link — a bundle-path submission must be as visible as an upload row.
  const [shippedBundle, setShippedBundle] =
    useState<GetSisBundleResponse | null>(null);
  // Log-viewer controls (local UI state; doesn't affect polling or fetch).
  const [logSearch, setLogSearch] = useState("");
  const [enabledLevels, setEnabledLevels] = useState<Set<EventLevel>>(
    () => new Set(ALL_LEVELS),
  );

  // Settings rarely change during a session, so a single fetch on mount
  // is enough. The Settings page itself will re-fetch when the user
  // toggles, and Rerun re-reads the live value server-side anyway.
  useEffect(() => {
    let cancelled = false;
    api
      .getSettings()
      .then((s) => {
        if (!cancelled) {
          setGlobalAutoSubmit(s.defaultSubmitToCanvas !== false);
          setExpectedTerms({
            primary: s.expectedPrimaryTerm,
            secondary: s.expectedSecondaryTerm,
          });
        }
      })
      .catch(() => {
        /* button falls back to "Rerun with same input" */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const sisBundleId = run?.sisBundleId;
  useEffect(() => {
    if (!sisBundleId) return;
    let cancelled = false;
    api
      .getSisBundle(sisBundleId)
      .then((b) => {
        if (!cancelled) setShippedBundle(b);
      })
      .catch(() => {
        /* panel falls back to an id-only link */
      });
    return () => {
      cancelled = true;
    };
  }, [sisBundleId]);

  usePoll(
    async () => {
      if (!runId) return;
      // Promise.allSettled so a transient uploads-list failure doesn't blank
      // out the run/events that already loaded. Run is the primary surface;
      // events are secondary; uploads are the "related panel" — each can fail
      // independently and we keep what we have.
      const [runRes, eventsRes, uploadsRes] = await Promise.allSettled([
        api.getRun(runId),
        api.getRunEvents(runId),
        api.listUploads(50, runId),
      ]);
      if (runRes.status === "fulfilled") setRun(runRes.value);
      if (eventsRes.status === "fulfilled") setEvents(eventsRes.value.events);
      if (uploadsRes.status === "fulfilled")
        setUploads(uploadsRes.value.uploads);

      if (runRes.status === "rejected") {
        setError(runRes.reason?.message ?? String(runRes.reason));
      } else if (eventsRes.status === "rejected") {
        setError(eventsRes.reason?.message ?? String(eventsRes.reason));
      } else {
        setError(null);
      }
      // Uploads errors are intentionally silent — see the comment above.
    },
    // Fast refresh while the run is in flight, OR while we're inside the
    // auto-submit window after a terminal SUCCEEDED run with submitToCanvas
    // on (so the inline "Auto-submit in progress…" message flips to "done"
    // promptly when the upload row lands). Slow safety refresh otherwise.
    () => {
      if (!run) return POLL.ACTIVE_MS;
      if (!TERMINAL.has(run.status)) return POLL.ACTIVE_MS;
      const autoSubmitOn = run.submitToCanvas === true;
      const noUploadsYet = uploads.length === 0;
      const finishedMs = run.finishedAt
        ? new Date(run.finishedAt).getTime()
        : 0;
      const insideAutoSubmitWindow =
        autoSubmitOn && noUploadsYet && Date.now() - finishedMs < 60_000;
      return insideAutoSubmitWindow ? POLL.ACTIVE_MS : POLL.IDLE_MS;
    },
    [runId],
  );

  async function handleDownload() {
    if (!runId) return;
    setDownloadErr(null);
    try {
      const { url } = await api.getRunOutputUrl(runId);
      setDownloadUrl(url);
      window.open(url, "_blank");
    } catch (e) {
      setDownloadErr((e as Error).message);
    }
  }

  async function handlePreviewInput() {
    if (!runId) return;
    setPreviewErr(null);
    setPreviewKind("input");
    setPreviewUrl(null);
    try {
      const { url } = await api.getRunInputUrl(runId);
      setPreviewUrl(url);
    } catch (e) {
      setPreviewErr((e as Error).message);
      setPreviewKind(null);
    }
  }

  async function handlePreviewOutput() {
    if (!runId) return;
    setPreviewErr(null);
    setPreviewKind("output");
    setPreviewUrl(null);
    try {
      const { url } = await api.getRunOutputUrl(runId);
      setPreviewUrl(url);
    } catch (e) {
      setPreviewErr((e as Error).message);
      setPreviewKind(null);
    }
  }

  function closePreview() {
    setPreviewUrl(null);
    setPreviewKind(null);
  }

  async function handleRerun() {
    if (!runId) return;
    setRerunErr(null);
    setRerunning(true);
    try {
      const result = await api.rerunRun(runId);
      nav(`/syncs/${result.runId}`);
    } catch (e) {
      setRerunErr((e as Error).message);
      setRerunning(false);
    }
  }

  // Gate the submit behind a confirm only when the fix is already in Canvas
  // via a SIS bundle; otherwise submit directly.
  function requestSubmitToCanvas() {
    if (run?.sisBundleFixIncluded === true) setConfirmSubmit(true);
    else void handleSubmitToCanvas();
  }

  async function handleSubmitToCanvas() {
    if (!runId) return;
    setSubmitErr(null);
    setSubmitting(true);
    try {
      const { response, canvasError } = await api.submitRunOutput(runId);
      // Canvas might have rejected the POST but the upload row still exists —
      // navigate to it so the user can see what happened either way.
      if (canvasError) setSubmitErr(canvasError);
      nav(`/uploads/${response.uploadId}`);
    } catch (e) {
      setSubmitErr((e as Error).message);
      setSubmitting(false);
    }
  }

  // Derived log filter. Must run on every render (before the early return
  // below) to satisfy the Rules of Hooks — its inputs don't depend on runId.
  const filteredEvents = useMemo(() => {
    const q = logSearch.trim().toLowerCase();
    if (!q && enabledLevels.size === ALL_LEVELS.length) return events;
    return events.filter((e) => {
      if (!enabledLevels.has(e.level)) return false;
      if (!q) return true;
      const hay =
        `${e.message} ${e.eventType ?? ""} ${e.step ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [events, logSearch, enabledLevels]);
  const isFiltered = filteredEvents.length !== events.length;

  if (!runId) return <div>Missing runId</div>;

  const ghostCount = extractGhostCount(events) ?? run?.counts?.ghostsFound;
  const coursesScanned = extractCoursesScanned(events);
  const verification = events.find((e) => e.eventType === "RUN_VERIFICATION");
  const reason = failureReason(run, events);
  const stuck = run ? isRunStuck(run) : false;

  function toggleLevel(level: EventLevel) {
    setEnabledLevels((prev) => {
      const next = new Set(prev);
      if (next.has(level)) next.delete(level);
      else next.add(level);
      return next;
    });
  }

  function handleDownloadLog() {
    if (events.length === 0) return;
    const ndjson = events.map((e) => JSON.stringify(e)).join("\n") + "\n";
    const blob = new Blob([ndjson], { type: "application/x-ndjson" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${runId}.events.ndjson`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <section>
      <div className="page-head">
        <div>
          <div className="muted small">
            <Link to="/syncs" className="muted-link">
              All syncs
            </Link>
          </div>
          <h1>Sync from {formatIdLabel(runId)}</h1>
          <div className="muted small mono" style={{ marginTop: 4 }}>
            {runId}
          </div>
        </div>
        {run && <StatusBadge status={run.status} />}
      </div>

      {error && <div className="error">{error}</div>}

      {stuck && run && <StuckWarningCard run={run} />}

      {reason && <FailureReasonCard reason={reason} />}

      {run?.autoSubmitError && !run.latestUploadId && (
        <AutoSubmitErrorCard
          message={run.autoSubmitError}
          at={run.autoSubmitErrorAt}
        />
      )}

      {run && (
        <div className="meta-grid">
          <Meta
            label="Canvas"
            value={
              run.canvasEnv ? (
                <span className={`env-tag env-tag-${run.canvasEnv}`}>
                  {run.canvasEnv}
                </span>
              ) : (
                "—"
              )
            }
          />
          <Meta
            label="Term"
            value={<TermDisplay run={run} expectedTerms={expectedTerms} />}
          />
          <Meta
            label="Started by"
            value={
              <>
                <span className="trigger-pill">
                  {triggerLabel(run.trigger)}
                </span>
                {run.triggerSource && (
                  <span className="muted small mono">
                    {" "}
                    · {run.triggerSource}
                  </span>
                )}
              </>
            }
          />
          <Meta label="Queued" value={formatDateTime(run.queuedAt)} />
          <Meta label="Started" value={formatDateTime(run.startedAt)} />
          <Meta label="Finished" value={formatDateTime(run.finishedAt)} />
          <Meta label="Duration" value={formatDuration(run.durationMs)} mono />
          <Meta label="Ghost enrollments" value={ghostCount ?? "—"} mono />
          <Meta label="Courses checked" value={coursesScanned ?? "—"} mono />
          <Meta
            label="Auto-submit to Canvas"
            value={
              run.submitToCanvas === true
                ? "On"
                : run.submitToCanvas === false
                  ? "Off"
                  : "—"
            }
          />
        </div>
      )}

      {run && <InputObjectCard run={run} onPreview={handlePreviewInput} />}

      {verification && <VerificationCard event={verification} />}

      <UploadsForRun
        uploads={uploads}
        runStatus={run?.status}
        sisBundleId={
          run?.sisBundleFixIncluded === true ? run.sisBundleId : undefined
        }
        sisBundle={shippedBundle}
      />

      {run && run.inputMd5 && (
        <RelatedRunsCard thisRun={run} relatedRuns={run.relatedRuns ?? []} />
      )}

      <div className="card">
        <div className="card-head">
          <h2>Activity log</h2>
          <div className="muted small">
            {isFiltered
              ? `${filteredEvents.length} of ${events.length} events`
              : `${events.length} event${events.length === 1 ? "" : "s"}`}
            {run && !TERMINAL.has(run.status) && (
              <> · refreshing every 5 seconds</>
            )}
          </div>
        </div>

        <div className="log-controls">
          <input
            type="text"
            className="filter-search log-search"
            placeholder="Search the log…"
            value={logSearch}
            onChange={(e) => setLogSearch(e.target.value)}
            aria-label="Search activity log"
            disabled={events.length === 0}
          />
          <div className="log-levels" role="group" aria-label="Log levels">
            <span className="filter-label">Levels</span>
            {ALL_LEVELS.map((lvl) => (
              <button
                key={lvl}
                type="button"
                className={
                  "filter-pill log-level-pill log-level-" +
                  lvl.toLowerCase() +
                  (enabledLevels.has(lvl) ? " filter-pill-active" : "")
                }
                onClick={() => toggleLevel(lvl)}
                aria-pressed={enabledLevels.has(lvl)}
              >
                {lvl}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="btn btn-sm"
            onClick={handleDownloadLog}
            disabled={events.length === 0}
            title="Download the full activity log as a .ndjson file"
          >
            Download log
          </button>
        </div>

        {events.length === 0 ? (
          <div className="muted">
            {run && TERMINAL.has(run.status)
              ? "No activity recorded yet. The log may still be loading — check back in a moment."
              : "Waiting for activity…"}
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="muted">No events match these filters.</div>
        ) : (
          <div className="event-stream">
            {filteredEvents.map((e, i) => (
              <EventRow key={i} event={e} />
            ))}
          </div>
        )}
      </div>

      {run && TERMINAL.has(run.status) && (
        <div className="actions">
          <button className="btn btn-primary" onClick={handleDownload}>
            Download fix CSV
          </button>
          {runHasOutput(run, ghostCount) && (
            <button
              type="button"
              className="btn"
              onClick={handlePreviewOutput}
              title="Preview the fix CSV inline (no download)"
            >
              Preview fix CSV
            </button>
          )}
          {isAdmin && (
            <SubmitToCanvasButton
              run={run}
              uploads={uploads}
              fixShippedViaBundle={run.sisBundleFixIncluded === true}
              hasOutput={runHasOutput(run, ghostCount)}
              submitting={submitting}
              onSubmit={requestSubmitToCanvas}
            />
          )}
          {isAdmin && (
            <button
              className="btn"
              onClick={handleRerun}
              disabled={rerunning || !run.inputS3Uri}
              title={
                !run.inputS3Uri
                  ? "This sync has no saved input — can't run again"
                  : globalAutoSubmit === true
                    ? `Run again with the same file. Auto-submit is on, so the new sync will send its fix CSV to Canvas (${run.canvasEnv ?? "test"}) automatically.`
                    : globalAutoSubmit === false
                      ? `Run again with the same file. Auto-submit is off, so nothing will be sent to Canvas until you submit it.`
                      : `Run again with the same file.`
              }
            >
              {rerunning
                ? "Starting…"
                : globalAutoSubmit === true
                  ? "Run again (auto-submits)"
                  : globalAutoSubmit === false
                    ? "Run again (no auto-submit)"
                    : "Run again with same file"}
            </button>
          )}
          {downloadErr && <div className="error inline">{downloadErr}</div>}
          {rerunErr && <div className="error inline">{rerunErr}</div>}
          {submitErr && <div className="error inline">{submitErr}</div>}
          {downloadUrl && !rerunErr && (
            <div className="muted small">
              Download link expires in 5 minutes.
            </div>
          )}
          {previewErr && <div className="error inline">{previewErr}</div>}
        </div>
      )}

      {previewKind && (
        <CsvPreviewModal
          title={
            previewKind === "input"
              ? "Input CSV (PeopleSoft enrollments)"
              : "Fix CSV (sent to Canvas)"
          }
          url={previewUrl}
          onClose={closePreview}
          onDownload={previewKind === "output" ? handleDownload : undefined}
        />
      )}

      {confirmSubmit && run && (
        <ConfirmDialog
          title="Fix already shipped via SIS bundle"
          message={
            `This sync's ghost-fix CSV was already sent to Canvas inside SIS bundle ` +
            `${run.sisBundleId ?? "(unknown)"}. Sending it again will re-post the same ` +
            `deletions as a separate SIS import. Only do this if you intend to re-apply it.`
          }
          confirmPhrase="submit again"
          confirmLabel="Send to Canvas anyway"
          cancelLabel="Cancel"
          danger
          busy={submitting}
          onConfirm={() => {
            setConfirmSubmit(false);
            void handleSubmitToCanvas();
          }}
          onCancel={() => setConfirmSubmit(false)}
        />
      )}
    </section>
  );
}

/** ─── Stuck-run warning (RUNNING for >60 min) ─────────────────────── */

function StuckWarningCard({ run }: { run: GetRunResponse }) {
  const execShort = run.stepFunctionsExecutionArn
    ? run.stepFunctionsExecutionArn.split(":").pop()
    : null;
  return (
    <div className="card stuck-card">
      <div className="card-head">
        <h2>This sync may be stuck</h2>
        <span className="badge badge-warn">
          In progress &gt; {stuckThresholdMinutes()} min
        </span>
      </div>
      <div className="stuck-detail">
        This sync has been running longer than expected. Something behind the
        scenes may have failed without reporting back. The dashboard will keep
        checking, but it can't recover this sync on its own — please contact an
        administrator if it doesn't finish soon.
      </div>
      <details className="raw-details">
        <summary>Show technical details</summary>
        <div className="raw-block" style={{ whiteSpace: "normal" }}>
          The run has been <code>RUNNING</code> for longer than the state
          machine's 30-minute timeout. The post-worker step that writes the
          terminal status likely never fired. Investigate the Step Functions
          execution
          {execShort && (
            <>
              {" "}
              (<span className="mono">{execShort}</span>)
            </>
          )}{" "}
          and the ECS task to find out what happened. If the worker is truly
          gone, manually update the run row to <code>FAILED</code> in DynamoDB.
        </div>
      </details>
    </div>
  );
}

/** ─── Failure Reason card ──────────────────────────────────────────── */

interface FailureReason {
  headline: string;
  details?: string;
  source: "errorMessage" | "RUN_FAILED-event" | "ERROR-event";
}

function failureReason(
  run: GetRunResponse | null,
  events: CanvasSyncRunEvent[],
): FailureReason | null {
  if (!run || !FAILURE_STATES.has(run.status)) return null;

  // The state-machine MarkFailed catch writes errorMessage on the run row.
  // That's the canonical headline; the RUN_FAILED event (if present) carries
  // the stack trace, which we tuck into a "Show details" disclosure.
  const failedEvent = events.find((e) => e.eventType === "RUN_FAILED");
  const failedStack =
    typeof failedEvent?.metadata?.stack === "string"
      ? (failedEvent.metadata.stack as string)
      : undefined;

  if (run.errorMessage) {
    return {
      headline: run.errorMessage,
      details: failedStack,
      source: "errorMessage",
    };
  }
  // Worker emitted RUN_FAILED but the state machine didn't write
  // errorMessage (e.g., the catch's $.error.Cause was empty). Use the event.
  if (failedEvent) {
    return {
      headline: failedEvent.message,
      details: failedStack,
      source: "RUN_FAILED-event",
    };
  }
  // No structured failure event — the run is FAILED but we only have
  // ERROR-level events to go on. Pull the most recent.
  const latestError = [...events].reverse().find((e) => e.level === "ERROR");
  if (latestError) {
    return {
      headline: latestError.message,
      source: "ERROR-event",
    };
  }
  return null;
}

function sourceLabel(s: FailureReason["source"]): string {
  switch (s) {
    case "errorMessage":
      return "reported by the sync";
    case "RUN_FAILED-event":
      return "reported by the worker";
    case "ERROR-event":
      return "from the activity log";
  }
}

/** ─── Auto-submit error card ────────────────────────────────────────── */

function AutoSubmitErrorCard({
  message,
  at,
}: {
  message: string;
  at?: string;
}) {
  return (
    <div className="card failure-card">
      <div className="card-head">
        <h2>Couldn't send to Canvas automatically</h2>
        {at && <span className="muted small">{formatDateTime(at)}</span>}
      </div>
      <div className="muted small" style={{ marginBottom: 8 }}>
        The sync finished, but the automatic submission to Canvas failed before
        an upload could be created. You can send the fix CSV manually with the
        "Send fix CSV to Canvas" button below.
      </div>
      <div className="failure-headline">{message}</div>
    </div>
  );
}

function FailureReasonCard({ reason }: { reason: FailureReason }) {
  return (
    <div className="card failure-card">
      <div className="card-head">
        <h2>Why it failed</h2>
        <span className="muted small">{sourceLabel(reason.source)}</span>
      </div>
      <div className="failure-headline">{reason.headline}</div>
      {reason.details && (
        <details className="raw-details">
          <summary>Show technical details</summary>
          <pre className="raw-block">{reason.details}</pre>
        </details>
      )}
    </div>
  );
}

/** ─── Run verification card ────────────────────────────────────────── */

function VerificationCard({ event }: { event: CanvasSyncRunEvent }) {
  const m = (event.metadata ?? {}) as VerificationMetadata;
  const integrityOk = m.integrityCheck?.ok !== false;
  const coursesFailed = m.enrollmentScan?.coursesFailed ?? 0;
  const coursesNotFound = m.enrollmentScan?.coursesNotFound ?? 0;
  const hasDrops = coursesFailed > 0 || coursesNotFound > 0;

  return (
    <div
      className={
        "card verification-card" +
        (!integrityOk ? " card-bad" : hasDrops ? " card-warn" : "")
      }
    >
      <div className="card-head">
        <h2>Sync results</h2>
        {!integrityOk && (
          <span className="badge badge-failed">Counts don't add up</span>
        )}
        {integrityOk && hasDrops && (
          <span className="badge badge-warn">Some courses were skipped</span>
        )}
        {integrityOk && !hasDrops && (
          <span className="badge badge-success">Looks good</span>
        )}
      </div>

      {!integrityOk && (
        <div className="error" style={{ marginBottom: 14 }}>
          The enrollment counts don't add up. This is likely a bug — please
          report it.{" "}
          <details style={{ display: "inline" }}>
            <summary style={{ display: "inline", cursor: "pointer" }}>
              Show numbers
            </summary>{" "}
            accounted={m.integrityCheck?.accounted ?? "?"} vs seen=
            {m.integrityCheck?.enrollmentsSeen ?? "?"}.
          </details>
        </div>
      )}
      {integrityOk && hasDrops && (
        <div className="warn-inline" style={{ marginBottom: 14 }}>
          {coursesFailed > 0 && (
            <>
              {coursesFailed} course{coursesFailed === 1 ? "" : "s"} couldn't be
              loaded.{" "}
            </>
          )}
          {coursesNotFound > 0 && (
            <>
              {coursesNotFound} PeopleSoft course
              {coursesNotFound === 1 ? "" : "s"}{" "}
              {coursesNotFound === 1 ? "wasn't" : "weren't"} found in
              Canvas.{" "}
            </>
          )}
          Ghost enrollments in those courses may have been missed.
        </div>
      )}

      <div className="verification-grid">
        <VerifSection title="From PeopleSoft">
          <Stat label="Courses" value={m.ps?.courses} />
          <Stat label="Sections" value={m.ps?.sections} />
          <Stat label="Students" value={m.ps?.students} />
        </VerifSection>

        <VerifSection title="Matched in Canvas">
          <Stat
            label="Courses found in Canvas"
            value={m.canvas?.psCoursesResolved}
          />
          <Stat
            label="Courses not found in Canvas"
            value={m.canvas?.psCoursesNotInCanvas}
            highlight={(m.canvas?.psCoursesNotInCanvas ?? 0) > 0}
          />
        </VerifSection>

        <VerifSection title="Courses checked">
          <Stat
            label="Courses checked"
            value={m.enrollmentScan?.coursesScanned}
          />
          <Stat
            label="Courses that failed to load"
            value={coursesFailed}
            highlight={coursesFailed > 0}
            tone="danger"
          />
          <Stat
            label="Courses not found in Canvas"
            value={coursesNotFound}
            highlight={coursesNotFound > 0}
            tone="warn"
          />
          {(m.enrollmentScan?.coursesRecoveredBySerialRetry ?? 0) > 0 && (
            <Stat
              label="Recovered after retry"
              value={m.enrollmentScan?.coursesRecoveredBySerialRetry}
              tone="success"
            />
          )}
        </VerifSection>

        <VerifSection title="Enrollment summary">
          <Stat
            label="Total enrollments checked"
            value={m.enrollmentScan?.enrollmentsSeen}
          />
          <Stat
            label="Ghost enrollments found"
            value={m.enrollmentScan?.ghostsFound}
            highlight={(m.enrollmentScan?.ghostsFound ?? 0) > 0}
            tone="warn"
          />
          <Stat
            label="Already in PeopleSoft"
            value={m.enrollmentScan?.alreadyInPs}
          />
          <Stat
            label="Skipped (no SIS ID)"
            value={m.enrollmentScan?.skippedNullSis}
          />
          <Stat
            label="Skipped (unknown section)"
            value={m.enrollmentScan?.skippedUnknownSection}
          />
          <Stat
            label="Skipped (added recently)"
            value={m.enrollmentScan?.skippedRecent}
          />
        </VerifSection>
      </div>
    </div>
  );
}

interface VerificationMetadata {
  ps?: { courses?: number; sections?: number; students?: number };
  canvas?: { psCoursesResolved?: number; psCoursesNotInCanvas?: number };
  enrollmentScan?: {
    coursesScanned?: number;
    coursesFailed?: number;
    coursesNotFound?: number;
    coursesRecoveredBySerialRetry?: number;
    coursesStillFailedAfterSerialRetry?: number;
    enrollmentsSeen?: number;
    ghostsFound?: number;
    alreadyInPs?: number;
    skippedNullSis?: number;
    skippedUnknownSection?: number;
    skippedRecent?: number;
  };
  integrityCheck?: {
    enrollmentsSeen?: number;
    accounted?: number;
    ok?: boolean;
  };
}

function VerifSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="verif-section">
      <div className="verif-section-title">{title}</div>
      <div className="verif-section-body">{children}</div>
    </div>
  );
}

function Stat({
  label,
  value,
  highlight,
  tone,
}: {
  label: string;
  value: number | undefined;
  highlight?: boolean;
  tone?: "success" | "warn" | "danger";
}) {
  const display = typeof value === "number" ? value.toLocaleString() : "—";
  return (
    <div
      className={
        "verif-stat" +
        (highlight ? " verif-stat-highlight" : "") +
        (tone ? ` verif-stat-${tone}` : "")
      }
    >
      <span className="verif-stat-label">{label}</span>
      <span className="verif-stat-value mono">{display}</span>
    </div>
  );
}

/** ─── Input object card ─────────────────────────────────────────────── */

function InputObjectCard({
  run,
  onPreview,
}: {
  run: GetRunResponse;
  onPreview: () => void;
}) {
  // Prefer the structured inputObject (populated for runs created via
  // either the API path or the S3-event path); fall back to the legacy
  // inputS3Uri string when the row predates that field.
  const io = run.inputObject;
  const fallbackUri = run.inputS3Uri;
  if (!io && !fallbackUri) return null;

  const bucket = io?.bucket ?? parseLegacyUri(fallbackUri)?.bucket;
  const key = io?.key ?? parseLegacyUri(fallbackUri)?.key;
  const size = io?.size;
  const lastModified = io?.lastModified;
  const eTag = io?.eTag;
  const versionId = io?.versionId;
  const fileName = key ? (key.split("/").pop() ?? key) : "—";

  return (
    <div className="card">
      <div className="card-head">
        <h2>Input file</h2>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            type="button"
            className="btn btn-sm"
            onClick={onPreview}
            title="Preview the first 200 rows in a modal"
          >
            Preview
          </button>
          <div className="muted small" style={{ maxWidth: 360 }}>
            The CSV file this sync used. Two syncs of the exact same file within
            5&nbsp;minutes are combined into one.
          </div>
        </div>
      </div>
      <div className="meta-grid">
        <Meta label="File name" value={fileName} mono small />
        <Meta
          label="Size"
          value={typeof size === "number" ? formatBytes(size) : "—"}
          mono
        />
        <Meta
          label="Last modified"
          value={lastModified ? formatDateTime(lastModified) : "—"}
        />
      </div>
      <details className="raw-details">
        <summary>Show technical details</summary>
        <div className="meta-grid" style={{ marginTop: 8 }}>
          <Meta label="S3 bucket" value={bucket ?? "—"} mono small />
          <Meta label="S3 key" value={key ?? "—"} mono small />
          {eTag && <Meta label="ETag" value={eTag} mono small />}
          {versionId && (
            <Meta label="Version ID" value={versionId} mono small />
          )}
        </div>
      </details>
    </div>
  );
}

function parseLegacyUri(
  uri: string | undefined,
): { bucket: string; key: string } | undefined {
  if (!uri) return undefined;
  const m = uri.match(/^s3:\/\/([^/]+)\/(.+)$/);
  if (!m || !m[1] || !m[2]) return undefined;
  return { bucket: m[1], key: m[2] };
}

/** ─── Uploads from this run ────────────────────────────────────────── */

function UploadsForRun({
  uploads,
  runStatus,
  sisBundleId,
  sisBundle,
}: {
  uploads: UploadSummary[];
  runStatus: string | undefined;
  /** Set when this run's fix shipped to Canvas inside a SIS bundle. */
  sisBundleId?: string;
  /** The fetched bundle (null while loading / on fetch failure). */
  sisBundle: GetSisBundleResponse | null;
}) {
  const bundleShipped = Boolean(sisBundleId);
  const count = uploads.length + (bundleShipped ? 1 : 0);
  return (
    <div className="card">
      <div className="card-head">
        <h2>Canvas submissions from this sync</h2>
        <div className="muted small">
          {count} {count === 1 ? "submission" : "submissions"}
        </div>
      </div>
      {count === 0 ? (
        <div className="muted small">
          {runStatus && TERMINAL.has(runStatus)
            ? "Nothing sent to Canvas from this sync yet. The hourly SIS bundle ships the fix automatically, or use “Send fix CSV to Canvas” below to send it standalone."
            : "Nothing sent yet. When this sync finishes, the fix ships via the next SIS bundle (or auto-submit, if on) — or you can send it yourself from this page."}
        </div>
      ) : (
        <ul className="related-uploads">
          {bundleShipped && sisBundleId && (
            <li key="sis-bundle" className="related-upload">
              {sisBundle ? (
                <SisBundleStatusBadge status={sisBundle.status} />
              ) : (
                <span className="trigger-pill">SIS bundle</span>
              )}
              <Link
                to={`/sis-bundles/${encodeURIComponent(sisBundleId)}`}
                className="run-link"
                title={sisBundleId}
              >
                {formatIdLabel(sisBundle?.dropId ?? sisBundleId)}
              </Link>
              <span
                className="trigger-pill"
                title="This sync's fix CSV went to Canvas inside this SIS bundle"
              >
                via SIS bundle
              </span>
              <span className="muted small">
                Canvas import ID:{" "}
                <span className="mono">{sisBundle?.sisImportId ?? "—"}</span>
              </span>
              <span className="muted small">
                {sisBundle?.submittedAt
                  ? formatRelative(sisBundle.submittedAt)
                  : "—"}
              </span>
            </li>
          )}
          {uploads.map((u) => (
            <li key={u.uploadId} className="related-upload">
              <UploadStatusBadge status={u.status} />
              <Link
                to={`/uploads/${u.uploadId}`}
                className="run-link"
                title={u.uploadId}
              >
                {formatIdLabel(u.uploadId)}
              </Link>
              <span className="trigger-pill">
                {uploadTriggerLabel(u.trigger)}
              </span>
              <span className="muted small">
                Canvas import ID:{" "}
                <span className="mono">{u.sisImportId ?? "—"}</span>
              </span>
              <span className="muted small">
                {formatRelative(u.submittedAt ?? u.queuedAt)}
              </span>
              <span className="muted small">
                {formatDuration(u.durationMs)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** ─── Existing bits ────────────────────────────────────────────────── */

function Meta({
  label,
  value,
  mono,
  small,
}: {
  label: string;
  value: string | number | React.ReactNode;
  mono?: boolean;
  small?: boolean;
}) {
  return (
    <div className="meta">
      <div className="meta-label">{label}</div>
      <div
        className={
          "meta-value" + (mono ? " mono" : "") + (small ? " small" : "")
        }
      >
        {value}
      </div>
    </div>
  );
}

function EventRow({ event }: { event: CanvasSyncRunEvent }) {
  const [open, setOpen] = useState(false);
  const hasMeta = event.metadata && Object.keys(event.metadata).length > 0;

  return (
    <div className={"event " + levelClass(event.level)}>
      <div className="event-head" onClick={() => hasMeta && setOpen(!open)}>
        <span className="event-time">{formatTime(event.timestamp)}</span>
        <span className={"event-level " + levelClass(event.level)}>
          {event.level}
        </span>
        {event.eventType && (
          <span className="event-type">{event.eventType}</span>
        )}
        <span className="event-message">{event.message}</span>
        {hasMeta && <span className="event-expand">{open ? "▾" : "▸"}</span>}
      </div>
      {open && hasMeta && (
        <div className="event-meta">
          {Object.entries(event.metadata!).map(([k, v]) => (
            <div key={k} className="kv">
              <span className="k">{k}</span>
              <span className="v">{formatMetaValue(v)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function levelClass(level: EventLevel): string {
  return "lvl-" + level.toLowerCase();
}

function extractGhostCount(events: CanvasSyncRunEvent[]): number | undefined {
  const e = events.find((x) => x.eventType === "GHOST_ENROLLMENTS_DETECTED");
  return typeof e?.metadata?.ghosts === "number"
    ? e.metadata.ghosts
    : undefined;
}

/**
 * Context-aware Submit button.
 *
 * Auto-submit is the canonical workflow — the worker's fix CSV pushes to
 * Canvas automatically after every successful run with submitToCanvas=true.
 * That makes a plain "Submit" button on the page redundant in the common
 * case. Three states the user actually encounters:
 *
 *   1. submitToCanvas was ON, run just finished, no upload row yet → the
 *      AutoSubmit Lambda is still spinning. Don't render a button (clicking
 *      it would create a duplicate upload). Render an inline progress hint.
 *      After ~60s of waiting we fall through to the primary button as a
 *      safety net in case AutoSubmit silently failed.
 *
 *   2. submitToCanvas was OFF (user opted out) and there's still no upload
 *      → primary button: "Submit fix CSV to Canvas". The only path to a
 *      submission for this run.
 *
 *   3. At least one upload already exists for this run — OR the fix already
 *      shipped to Canvas inside a SIS bundle → demoted secondary button:
 *      "Send again". The bundle case matters: there's no upload row for a
 *      bundle-path submission, and rendering the PRIMARY button then reads
 *      as "nothing was sent", which is exactly how operators end up
 *      double-posting a fix that already went out.
 */
function SubmitToCanvasButton({
  run,
  uploads,
  fixShippedViaBundle,
  hasOutput,
  submitting,
  onSubmit,
}: {
  run: GetRunResponse;
  uploads: UploadSummary[];
  fixShippedViaBundle: boolean;
  hasOutput: boolean;
  submitting: boolean;
  onSubmit: () => void;
}) {
  if (!hasOutput) return null;

  const env = (run.canvasEnv ?? "test").toUpperCase();
  const hasUpload = uploads.length > 0 || fixShippedViaBundle;
  const autoSubmitOn = run.submitToCanvas === true;
  const finishedMs = run.finishedAt
    ? new Date(run.finishedAt).getTime()
    : Date.now();
  const sinceFinishedMs = Date.now() - finishedMs;
  // AutoSubmit Lambda usually takes <15s; we wait 60s before assuming it
  // failed silently and offering the manual button as a fallback.
  const AUTO_SUBMIT_WINDOW_MS = 60_000;
  const waitingForAutoSubmit =
    autoSubmitOn && !hasUpload && sinceFinishedMs < AUTO_SUBMIT_WINDOW_MS;

  if (waitingForAutoSubmit) {
    return (
      <div className="muted small inline-progress">
        <span className="badge-pulse" /> Sending fix CSV to Canvas ({env})…
      </div>
    );
  }

  return (
    <button
      className={hasUpload ? "btn btn-sm" : "btn"}
      onClick={onSubmit}
      disabled={submitting}
      title={
        fixShippedViaBundle && uploads.length === 0
          ? `This sync's fix already went to Canvas inside a SIS bundle. Sending again creates a duplicate standalone upload — you'll be asked to confirm.`
          : hasUpload
            ? `Send this sync's fix CSV to Canvas (${run.canvasEnv ?? "test"}) again as a new upload. The previous submission stays in upload history.`
            : `Send this sync's fix CSV to Canvas (${run.canvasEnv ?? "test"}) as a new upload.`
      }
    >
      {submitting
        ? "Sending…"
        : hasUpload
          ? `Send again to Canvas (${env})`
          : `Send fix CSV to Canvas (${env})`}
    </button>
  );
}

/**
 * Does this run have a fix CSV available to submit?
 *
 * Conservative: any positive signal counts. We don't want to hide the
 * submit button on a run that actually has an output just because one
 * of the signals is missing (e.g. older runs predate `outputS3Uri`
 * persistence; events may not yet be loaded on the first poll).
 *
 * The /api/runs/{runId}/submit endpoint validates existence server-side
 * and returns 404 if the object isn't actually there — we never block on
 * a false negative here.
 */
function runHasOutput(
  run: GetRunResponse,
  ghostCount: number | undefined,
): boolean {
  if (run.outputS3Uri) return true;
  if (typeof ghostCount === "number" && ghostCount > 0) return true;
  if ((run.counts?.ghostsFound ?? 0) > 0) return true;
  return false;
}

function extractCoursesScanned(
  events: CanvasSyncRunEvent[],
): number | undefined {
  const e = events.find((x) => x.eventType === "GHOST_ENROLLMENTS_DETECTED");
  return typeof e?.metadata?.coursesScanned === "number"
    ? e.metadata.coursesScanned
    : undefined;
}

/**
 * Compare this run against other runs that processed the same enrollment
 * file (matched by inputMd5 + canvasEnv). Surfaces the "we ran the same
 * file twice and got different ghost counts" bug signal.
 *
 * Hidden when the run has no inputMd5 (pre-feature runs or runs that
 * failed before hashing finished). Shown but with a "no other runs found"
 * note when this is the first run against the file.
 */
function RelatedRunsCard({
  thisRun,
  relatedRuns,
}: {
  thisRun: GetRunResponse;
  relatedRuns: RelatedRunSummary[];
}) {
  // Verdict is computed against PRIOR runs only — a later re-run finding
  // fewer ghosts (fix applied in between) must not retroactively put the
  // "found more than a prior run" alert on this (earlier) run. The table
  // below still lists ALL sibling runs for navigation.
  const priorRuns = relatedRuns.filter((r) => r.queuedAt < thisRun.queuedAt);
  const comparison = compareToGroup(
    {
      runId: thisRun.runId,
      status: thisRun.status,
      canvasEnv: thisRun.canvasEnv,
      inputMd5: thisRun.inputMd5,
      ghostsFound: thisRun.counts?.ghostsFound,
    },
    priorRuns.map((r) => ({
      runId: r.runId,
      status: r.status,
      canvasEnv: r.canvasEnv,
      inputMd5: thisRun.inputMd5,
      ghostsFound: r.ghostsFound,
    })),
  );

  const isHigher = comparison.kind === "higher";

  return (
    <div className={"card" + (isHigher ? " failure-card" : "")}>
      <div className="card-head">
        <h2>Other runs with this input</h2>
        <div className="muted small">Matched by md5 of the enrollment file</div>
      </div>

      {isHigher && (
        <div className="error" style={{ marginBottom: 12 }}>
          This run found <strong>{comparison.thisCount}</strong> ghosts. A prior
          run against the same file found only <strong>{comparison.min}</strong>
          . Detection should be deterministic for the same input — investigate
          whether the algorithm missed rows on the earlier run, or whether
          something between PS and Canvas has changed in a way that produces new
          ghosts.
        </div>
      )}

      {comparison.kind === "lower" && (
        <div className="muted" style={{ marginBottom: 8 }}>
          This run found {comparison.thisCount} ghost
          {comparison.thisCount === 1 ? "" : "s"} — fewer than a prior run (
          {comparison.min}). Usually means an earlier fix file was applied
          successfully in Canvas.
        </div>
      )}

      {comparison.kind === "same" && priorRuns.length > 0 && (
        <div className="muted" style={{ marginBottom: 8 }}>
          Ghost count matches prior runs against this file.
        </div>
      )}

      {relatedRuns.length === 0 ? (
        <div className="muted">
          No other runs have processed this enrollment file yet. The comparison
          will populate on subsequent runs.
        </div>
      ) : (
        <table className="runs-table">
          <thead>
            <tr>
              <th>Run</th>
              <th>Status</th>
              <th>Ghosts</th>
              <th>Upload</th>
              <th>Finished</th>
            </tr>
          </thead>
          <tbody>
            {relatedRuns.map((r) => (
              <tr key={r.runId}>
                <td>
                  <Link to={`/syncs/${r.runId}`}>{formatIdLabel(r.runId)}</Link>
                </td>
                <td>
                  <StatusBadge status={r.status} />
                </td>
                <td>
                  <code>
                    {r.ghostsFound !== undefined ? r.ghostsFound : "—"}
                  </code>
                </td>
                <td>
                  {r.latestUploadStatus ? (
                    <UploadStatusBadge status={r.latestUploadStatus} />
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td title={r.finishedAt ? formatDateTime(r.finishedAt) : "—"}>
                  {r.finishedAt ? formatRelative(r.finishedAt) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {thisRun.inputMd5 && (
        <div
          className="muted small"
          style={{ marginTop: 8, fontFamily: "var(--mono)" }}
        >
          md5: {thisRun.inputMd5}
        </div>
      )}
    </div>
  );
}
